import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, BarChart3, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import AdminTabs from "@/components/AdminTabs";

const EVENT_LABELS = {
  landing_view: "Landed on homepage",
  register_start: "Started register",
  register_complete: "Created account",
  login_success: "Signed in",
  first_tile_added: "First caregiver tile",
  tile_template_applied: "Applied a board template",
  checkout_start: "Opened checkout",
  checkout_complete: "Completed checkout",
  day_1_return: "Returned next day",
};

const FUNNEL_GROUPS = [
  { title: "Acquisition", events: ["landing_view", "register_start", "register_complete"] },
  { title: "Activation", events: ["login_success", "first_tile_added", "tile_template_applied"] },
  { title: "Revenue", events: ["checkout_start", "checkout_complete"] },
  { title: "Retention", events: ["day_1_return"] },
];

function pct(numer, denom) {
  if (!denom) return null;
  return Math.round((numer / denom) * 100);
}

export default function AdminFunnel() {
  useSEO({ title: "Admin · Funnel analytics", noindex: true });
  const [days, setDays] = useState(14);
  const [funnel, setFunnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const reload = async () => {
    setError("");
    setLoading(true);
    try {
      const r = await api.get(`/admin/funnel?days=${days}`);
      setFunnel(r.data?.funnel || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [days]);

  const byEvent = Object.fromEntries(funnel.map((f) => [f.event, f]));
  const landing = byEvent.landing_view?.unique_sessions || 0;

  return (
    <div className="min-h-screen bg-[color:var(--cream)] px-5 sm:px-10 py-6">
      <div className="max-w-5xl mx-auto">
        <Link to="/admin/region" className="pill-btn mb-4 inline-flex" data-testid="back-to-admin">
          <ArrowLeft size={14} /> Back
        </Link>
        <AdminTabs current="funnel" />

        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <h1 className="display text-3xl sm:text-4xl font-extrabold flex items-center gap-3">
            <BarChart3 size={32} className="text-[color:var(--coral)]" />
            Funnel
          </h1>
          <div className="flex gap-2 items-center">
            <select
              className="brutal-input w-auto"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              data-testid="funnel-days-picker"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <button type="button" onClick={reload} className="pill-btn" data-testid="funnel-reload">
              <RefreshCw size={12} />
            </button>
          </div>
        </div>
        <p className="text-[color:var(--ink-soft)] text-sm mb-6 max-w-3xl">
          Server-side, privacy-first. Counts unique sessions and unique users per
          event, plus the conversion ratio against landing-page sessions. No
          Google / Facebook / Mixpanel.
        </p>

        {error && (
          <div className="brutal-card bg-[#FFE3E0] text-[color:var(--coral-deep)] p-3 mb-4" data-testid="funnel-error">
            {error}
          </div>
        )}

        {loading ? (
          <p>Loading…</p>
        ) : (
          <div className="space-y-6">
            {FUNNEL_GROUPS.map((grp) => (
              <section key={grp.title} className="brutal-card bg-white p-5">
                <div className="display text-xs uppercase tracking-[0.25em] text-[color:var(--coral-deep)] font-bold mb-3">
                  {grp.title}
                </div>
                <div className="space-y-2">
                  {grp.events.map((evName) => {
                    const f = byEvent[evName] || { events: 0, unique_sessions: 0, unique_users: 0 };
                    const conv = pct(f.unique_sessions, landing);
                    const widthPct = landing ? Math.max(2, (f.unique_sessions / landing) * 100) : 0;
                    return (
                      <div key={evName} data-testid={`funnel-row-${evName}`}>
                        <div className="flex items-baseline justify-between gap-2 mb-1">
                          <div className="text-sm font-bold">{EVENT_LABELS[evName] || evName}</div>
                          <div className="text-xs text-[color:var(--ink-soft)] tabular-nums">
                            {f.unique_sessions} sessions · {f.unique_users} users · {f.events} events
                            {conv !== null && evName !== "landing_view" && (
                              <span className="ml-2 font-bold text-[color:var(--coral-deep)]">{conv}%</span>
                            )}
                          </div>
                        </div>
                        <div className="h-2.5 bg-[color:var(--paper-muted)] rounded-full overflow-hidden border border-[color:var(--ink-soft)]/20">
                          <div
                            className="h-full bg-[color:var(--coral)] rounded-full transition-[width]"
                            style={{ width: `${widthPct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}

            {!landing && (
              <div className="brutal-card bg-[color:var(--paper-muted)] p-4 text-sm text-[color:var(--ink-soft)]">
                No landing_view events in the last {days} days yet. Once visitors
                hit the homepage with the new analytics SDK in place, this funnel
                will populate.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
